import React, { useState, useEffect } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Stack, Skeleton, Alert, useTheme,
} from "@mui/material";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { botsApi } from "../../../api/bots.js";
import { MetricCard } from "../../../components/MetricCard.js";
import { CHART_COLORS } from "../../../theme/index.js";

interface DailyPoint {
  date: string;
  messages: number;
  tokens_input: number;
  tokens_output: number;
}

interface Analytics {
  period: string;
  document_count: number;
  total_chunks: number;
  messages_used: number;
  messages_limit: number;
  tokens_used_input: number;
  tokens_used_output: number;
  daily_usage: DailyPoint[];
}

function shortDate(iso: string) {
  const [, m, d] = iso.split("-");
  return `${parseInt(m)}/${parseInt(d)}`;
}

export function AnalyticsTab({ botId }: { botId: string }) {
  const theme = useTheme();
  const grid  = theme.palette.divider;
  const [data, setData]   = useState<Analytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    botsApi.getAnalytics(botId).then(setData).catch(() => setError("Failed to load analytics"));
  }, [botId]);

  if (error) return <Alert severity="error">{error}</Alert>;

  if (!data) return (
    <Grid container spacing={2}>
      {[1,2,3,4].map(i => <Grid item xs={6} md={3} key={i}><Skeleton variant="rounded" height={96} /></Grid>)}
    </Grid>
  );

  const msgPct = data.messages_limit > 0 ? Math.round((data.messages_used / data.messages_limit) * 100) : 0;

  // Trim leading all-zero days so the chart starts where data does
  const daily = data.daily_usage ?? [];
  const firstNonZero = daily.findIndex(d => d.messages > 0 || d.tokens_input > 0);
  const chartData = firstNonZero >= 0 ? daily.slice(firstNonZero) : daily.slice(-7);

  const hasData = chartData.some(d => d.messages > 0 || d.tokens_input > 0);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Analytics</Typography>
        <Typography variant="caption" color="text.secondary">Period: {data.period}</Typography>
      </Stack>

      {/* Metric cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <MetricCard label="Messages used" value={data.messages_used.toLocaleString()} accent="yellow">
            <Typography variant="caption" color="text.secondary">
              {`of ${data.messages_limit.toLocaleString()} (${msgPct}%)`}
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Data Sources" value={data.document_count} accent="cyan">
            <Typography variant="caption" color="text.secondary">
              {`${data.total_chunks.toLocaleString()} chunks indexed`}
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Input tokens" value={data.tokens_used_input.toLocaleString()} accent="cyan" />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Output tokens" value={data.tokens_used_output.toLocaleString()} accent="yellow" />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" mb={2}>Messages per day</Typography>
              {!hasData ? (
                <Box height={220} display="flex" alignItems="center" justifyContent="center">
                  <Typography variant="body2" color="text.secondary">No data yet</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                    <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      labelFormatter={shortDate}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${grid}` }}
                    />
                    <Line
                      type="monotone" dataKey="messages" name="Messages"
                      stroke={CHART_COLORS[0]} strokeWidth={2} dot={false} activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" mb={2}>Token usage per day</Typography>
              {!hasData ? (
                <Box height={220} display="flex" alignItems="center" justifyContent="center">
                  <Typography variant="body2" color="text.secondary">No data yet</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                    <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      labelFormatter={shortDate}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${grid}` }}
                      formatter={(v: number) => v.toLocaleString()}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line
                      type="monotone" dataKey="tokens_input" name="Input"
                      stroke={CHART_COLORS[1]} strokeWidth={2} dot={false} activeDot={{ r: 4 }}
                    />
                    <Line
                      type="monotone" dataKey="tokens_output" name="Output"
                      stroke={CHART_COLORS[2]} strokeWidth={2} dot={false} activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
